import { fmtDate } from "@/lib/dateFormat";
import { useEffect, useState } from "react";
import { Receipt } from "lucide-react";
import { api, money } from "@/lib/api";
import { StatusChip } from "@/components/StatusChip";

/**
 * Read-only list of expenses linked to a customer / vehicle / supplier / job.
 * Pass exactly one of customerId / vehicleId / supplierId / jobId.
 */
export function RelatedExpenses({ customerId, vehicleId, supplierId, jobId, title = "Related expenses" }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams();
    if (customerId) p.set("customer_id", customerId);
    if (vehicleId) p.set("vehicle_id", vehicleId);
    if (supplierId) p.set("supplier_id", supplierId);
    if (jobId) p.set("job_id", jobId);
    if (!p.toString()) return;
    api(`/expenses?${p.toString()}`).then((r) => setRows(r || [])).catch(() => {}).finally(() => setLoaded(true));
  }, [customerId, vehicleId, supplierId, jobId]);

  if (!loaded || rows.length === 0) return null;
  const total = rows.reduce((s, e) => s + (Number(e.total) || 0), 0);
  const outstanding = rows.reduce((s, e) => s + (Number(e.outstanding) || 0), 0);

  return (
    <div className="mt-6 rounded-xl border border-border bg-card p-5" data-testid="related-expenses">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2"><Receipt className="h-5 w-5 text-brand" /><h3 className="font-display text-base font-bold uppercase text-midnight">{title}</h3></div>
        <div className="text-right text-xs text-muted-foreground">Total {money(total)}{outstanding > 0 ? <> · <span className="font-semibold text-warning">Outstanding {money(outstanding)}</span></> : null}</div>
      </div>
      <div className="divide-y divide-border">
        {rows.map((e) => (
          <div key={e.id} data-testid={`related-expense-${e.id}`} className="flex items-center gap-3 py-2.5 text-sm">
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-midnight">{e.category}{e.supplier_name ? ` · ${e.supplier_name}` : ""}</p>
              <p className="truncate text-xs text-muted-foreground">{fmtDate(e.date)}{e.description ? ` · ${e.description}` : ""}</p>
            </div>
            <p className="font-display font-bold text-midnight">{money(e.total)}</p>
            <StatusChip status={e.payment_status === "partial" ? "part_paid" : e.payment_status || "paid"} />
          </div>
        ))}
      </div>
    </div>
  );
}
